import React, { useState, useRef, useEffect, type ReactNode } from "react";
import Dropdown from "../../../../../../../../../components/Dropdown/Dropdown";
import MultiSelectSearchableDropdown from "../../../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SingleDatePicker from "../../../../../../../../../components/SingleDatePicker/SingleDatePicker";
import Input from "../../../../../../../../../components/Input/Input";
import { TruncatedTextTooltip } from "../../../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import CalendarIcon from "../../../../../../../../../assets/svg/calendar-icon";
import SearchIcon from "../../../../../../../../../assets/svg/search-icon";
import {
  HelpdeskCustomFieldDefinition,
  HELPDESK_CUSTOM_FIELD_TYPES,
  getEffectiveDropdownOptions,
  normalizeHelpdeskFieldType,
  orderCustomFieldsForRender,
} from "../utils/helpdeskCustomFields";
import { getSystemIcon } from "../utils/systemIcons";
import { useDebouncedCallback } from "../../../../../../../../../hooks/useDebounce";
import { PermissionDisabledWrapper } from "../../../../PermissionDisabledWrapper";
import { FEATURE_REQUESTS_SYSTEM_NAME } from "../../../../CreateTicket/utils/constants";

const WRAP_CLASS = "flex flex-col gap-1 min-w-0";
const DROPDOWN_TRIGGER =
  "!rounded-lg !h-[26px] !bg-transparent dark:!bg-transparent !border-0 hover:!bg-hover-effect dark:hover:!bg-hover-effect-dk";
const DROPDOWN_SELECTED = "!px-1.5 !py-0 h-full min-w-0";
const DROPDOWN_LABEL_TRUNCATE_CLASS =
  "text-md font-medium text-verba-indigo dark:text-verba-indigo-dk";

function optionNodes(label: string, icon?: React.ReactNode) {
  if (icon) {
    const content = (
      <span className="flex items-center gap-2 min-w-0 max-w-full">
        <span className="shrink-0">{icon}</span>
        <TruncatedTextTooltip
          text={label}
          maxWidth="max-w-full min-w-0"
          className={`${DROPDOWN_LABEL_TRUNCATE_CLASS} min-w-0`}
        />
      </span>
    );
    return { content, selectedOptionContent: content };
  }
  return {
    content: (
      <TruncatedTextTooltip
        text={label}
        maxWidth="max-w-full min-w-0"
        className={DROPDOWN_LABEL_TRUNCATE_CLASS}
      />
    ),
    selectedOptionContent: (
      <TruncatedTextTooltip
        text={label}
        maxWidth="max-w-full min-w-0"
        className={`${DROPDOWN_LABEL_TRUNCATE_CLASS} min-w-0`}
      />
    ),
  };
}

function EditableTextField({
  fieldKey,
  value,
  placeholder,
  label,
  onChange,
  type = "text",
  disabled = false,
}: {
  fieldKey: string;
  value: string;
  placeholder: string;
  label: ReactNode;
  onChange: (fieldKey: string, value: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && containerRef.current) {
      const input = containerRef.current?.querySelector("input");
      input?.focus();
    }
  }, [isEditing]);

  const debouncedOnChange = useDebouncedCallback(
    (val: string) => onChange(fieldKey, val),
    500
  );

  return (
    <div className="flex flex-col min-w-0 text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk gap-1">
      {label}
      <PermissionDisabledWrapper disabled={disabled} className="w-max">
        {isEditing ? (
          <div ref={containerRef}>
            <Input
              name={fieldKey}
              type={type}
              value={localValue}
              onChange={(e) => {
                setLocalValue(e.target.value);
                debouncedOnChange(e.target.value);
              }}
              onBlur={() => setIsEditing(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape")
                  setIsEditing(false);
              }}
              placeholder={placeholder}
              containerClassName="!gap-0.5"
              labelClassName="hidden"
              secondaryContainerClassName="min-h-[26px] px-2 rounded-lg border border-border-gray dark:border-border-gray-dk bg-transparent"
              inputClassName="!text-md [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
            />
          </div>
        ) : (
          <span
            className="relative -top-1 left-0 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk truncate cursor-pointer flex items-center h-[26px] border border-transparent"
            onClick={() => setIsEditing(true)}
          >
            {value || (
              <span className="text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                {placeholder}
              </span>
            )}
          </span>
        )}
      </PermissionDisabledWrapper>
    </div>
  );
}

const DIRECT_ISSUE_CATEGORY_MAP: Record<string, string> = {
  sales: "Sales",
  marketing: "Marketing",
};

export interface HelpdeskTicketDynamicFieldsProps {
  fieldDefinitions: HelpdeskCustomFieldDefinition[] | undefined;
  values: Record<string, unknown>;
  onFieldChange: (fieldKey: string, value: unknown) => void;
  disabled?: boolean;
  ticketCategory?: string;
}

export function HelpdeskTicketDynamicFields({
  fieldDefinitions,
  values,
  onFieldChange,
  disabled = false,
  ticketCategory,
}: HelpdeskTicketDynamicFieldsProps) {
  const ordered = orderCustomFieldsForRender(fieldDefinitions);

  const [systemSearch, setSystemSearch] = useState("");
  const [issueSearch, setIssueSearch] = useState("");

  const directIssueSystemName = DIRECT_ISSUE_CATEGORY_MAP[ticketCategory ?? ""] ?? null;

  const renderField = (field: HelpdeskCustomFieldDefinition) => {
    if (
      field?.key === "issue-type" &&
      values?.["system-name"] === FEATURE_REQUESTS_SYSTEM_NAME
    ) {
      return null;
    }
    if (directIssueSystemName && field?.key === "system-name") {
      return null;
    }
    const t = normalizeHelpdeskFieldType(field?.type);
    const labelEl = (
      <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk inline-block max-w-full truncate">
        {field?.name}
      </span>
    );

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE) {
      const allOptions = getEffectiveDropdownOptions(field, values);
      const raw = values?.[field?.key];
      const current =
        typeof raw === "string" || raw === null || raw === undefined
          ? ((raw as string | null) ?? "")
          : String(raw);

      const isSystemNameField = field?.key === "system-name";
      const isIssueTypeField = field?.key === "issue-type";
      const isSearchableField = isSystemNameField || isIssueTypeField;

      const searchQuery = isSystemNameField ? systemSearch : isIssueTypeField ? issueSearch : "";
      const filteredOptions = isSearchableField && searchQuery.trim()
        ? allOptions.filter((opt) => opt.toLowerCase().includes(searchQuery.trim().toLowerCase()))
        : allOptions;

      let searchMenu: React.ReactNode | undefined;

      if (isSystemNameField) {
        searchMenu = (
          <div className="flex flex-col border-b border-border-gray dark:border-border-gray-dk">
            <div className="flex items-center gap-1.5 px-2.5 py-2">
              <SearchIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              <input
                type="text"
                value={systemSearch}
                onChange={(e) => setSystemSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search system names..."
                className="flex-1 text-sm bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
                autoFocus
              />
            </div>
          </div>
        );
      }

      if (isIssueTypeField) {
        searchMenu = (
          <div className="flex flex-col border-b border-border-gray dark:border-border-gray-dk">
            <div className="flex items-center gap-1.5 px-2.5 py-2">
              <SearchIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              <input
                type="text"
                value={issueSearch}
                onChange={(e) => setIssueSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search issue types..."
                className="flex-1 text-sm bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
                autoFocus
              />
            </div>
          </div>
        );
      }

      const handleChange = (val: string) => {
        onFieldChange(field?.key, val);
        if (isSystemNameField) setSystemSearch("");
        if (isIssueTypeField) setIssueSearch("");
      };

      return (
        <div key={field?.key} className="flex flex-col min-w-0">
          {labelEl}
          <PermissionDisabledWrapper disabled={disabled} className="w-max">
            <Dropdown
              key={`ticket-cf-${field?.key}`}
              id={`ticket-cf-${field?.key}`}
              options={filteredOptions?.map((opt) => ({
                value: opt,
                ...optionNodes(
                  opt,
                  isSystemNameField ? getSystemIcon(opt) : undefined
                ),
              }))}
              value={current ? current : null}
              placeholder="Select one option"
              placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
              onChange={handleChange}
              className="w-max max-w-full relative right-1.5"
              triggerClassName={DROPDOWN_TRIGGER}
              selectedOptionContainerClassName={DROPDOWN_SELECTED}
              dropdownClassName="min-w-max !bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
              selectedOptionTextClassName="!text-md !min-w-0"
              triggerIconClassName={
                allOptions?.includes(current)
                  ? "!text-heading-text dark:!text-heading-text-dk"
                  : "!text-gray-placeholder dark:!text-gray-placeholder-dk"
              }
              ariaLabel={field?.name}
              {...(searchMenu ? { dropdownMenuTitle: searchMenu } : {})}
            />
          </PermissionDisabledWrapper>
        </div>
      );
    }

    if (
      t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI ||
      t === HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX
    ) {
      const options = getEffectiveDropdownOptions(field, values);
      const raw = values?.[field?.key];
      const selected = Array.isArray(raw) ? (raw as string[])?.map(String) : [];

      const tagOptions = options?.map((opt) => ({
        value: opt,
        label: opt,
        selectedOptionContent: opt,
        content: (
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
            {opt}
          </span>
        ),
      }));

      const idPrefix =
        t === HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX
          ? "ticket-cf-checkbox"
          : "ticket-cf-multi";

      return (
        <div key={field?.key} className="flex flex-col min-w-0">
          {labelEl}
          <PermissionDisabledWrapper disabled={disabled} className="w-max">
            <MultiSelectSearchableDropdown
              id={`${idPrefix}-${field?.key}`}
              options={tagOptions}
              value={selected}
              onChange={(vals) => onFieldChange(field?.key, vals)}
              placeholder="Select options"
              showSelectedOptionsChips={true}
              autoFitChips
              moreTagText=""
              showSearch={options?.length > 5}
              searchPlaceholder="Search options..."
              className="w-max max-w-full relative right-1.5"
              triggerClassName={DROPDOWN_TRIGGER}
              inputContainerClassName={DROPDOWN_SELECTED}
              placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
              triggerIconClassName={
                selected?.length === 0
                  ? "!text-gray-placeholder dark:!text-gray-placeholder-dk"
                  : "!text-heading-text dark:!text-heading-text-dk"
              }
              chipClassName="inline-flex items-center px-0 py-0 text-md font-medium rounded-none border-none bg-transparent text-heading-text dark:text-heading-text-dk shrink min-w-0 [&>*:last-child]:hidden after:content-[','] last:after:content-[''] after:ml-[1.5px]"
              moreTagClassName="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-tag-gray dark:bg-tag-gray-dk text-heading-text dark:text-heading-text-dk shrink-0 font-medium"
              dropdownClassName="min-w-max !bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
              optionsListClassName="!py-0"
              ariaLabel={field?.name}
            />
          </PermissionDisabledWrapper>
        </div>
      );
    }

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.TEXT) {
      const v = values?.[field?.key];
      const str = v == null ? "" : String(v);

      return (
        <EditableTextField
          key={field?.key}
          fieldKey={field?.key}
          value={str}
          placeholder="Enter text"
          label={labelEl}
          onChange={onFieldChange}
          disabled={disabled}
        />
      );
    }

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.LONG_TEXT) {
      const v = values?.[field?.key];
      const str = v == null ? "" : String(v);
      return (
        <EditableTextField
          key={field?.key}
          fieldKey={field?.key}
          value={str}
          placeholder="Enter text"
          label={labelEl}
          onChange={onFieldChange}
          disabled={disabled}
        />
      );
    }

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.RADIO) {
      const options = getEffectiveDropdownOptions(field, values);
      const raw = values?.[field?.key];
      const current =
        typeof raw === "string" || raw === null || raw === undefined
          ? ((raw as string | null) ?? "")
          : String(raw);

      return (
        <div key={field?.key} className="flex flex-col min-w-0">
          {labelEl}
          <PermissionDisabledWrapper disabled={disabled} className="w-max">
            <Dropdown
              id={`ticket-cf-radio-${field?.key}`}
              options={options?.map((opt) => {
                const isSelected = current === opt;
                const content = (
                  <span className="flex items-center gap-2 min-w-0 max-w-full">
                    <span
                      className={`shrink-0 w-3 h-3 rounded-full border flex items-center justify-center transition-colors ${
                        isSelected
                          ? "border-verba-blue dark:border-verba-blue-dk"
                          : "border-gray-placeholder dark:border-gray-placeholder-dk"
                      }`}
                    >
                      {isSelected && (
                        <span className="w-[5px] h-[5px] rounded-full bg-verba-blue dark:bg-verba-blue-dk" />
                      )}
                    </span>
                    <TruncatedTextTooltip
                      text={opt}
                      maxWidth="max-w-full min-w-0"
                      className={`${DROPDOWN_LABEL_TRUNCATE_CLASS} min-w-0`}
                    />
                  </span>
                );
                return {
                  value: opt,
                  content,
                  selectedOptionContent: (
                    <TruncatedTextTooltip
                      text={opt}
                      maxWidth="max-w-full min-w-0"
                      className={`${DROPDOWN_LABEL_TRUNCATE_CLASS} min-w-0`}
                    />
                  ),
                };
              })}
              value={current ? current : null}
              placeholder="Select one option"
              placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
              onChange={(val) => onFieldChange(field?.key, val)}
              className="w-max max-w-full relative right-1.5"
              triggerClassName={DROPDOWN_TRIGGER}
              selectedOptionContainerClassName={DROPDOWN_SELECTED}
              dropdownClassName="min-w-max !bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
              selectedOptionTextClassName="!text-md !min-w-0"
              triggerIconClassName={
                options?.includes(current)
                  ? "!text-heading-text dark:!text-heading-text-dk"
                  : "!text-gray-placeholder dark:!text-gray-placeholder-dk"
              }
              ariaLabel={field?.name}
            />
          </PermissionDisabledWrapper>
        </div>
      );
    }

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.NUMBER) {
      const v = values?.[field?.key];
      const str = v == null || v === "" ? "" : String(v);
      return (
        <EditableTextField
          key={field?.key}
          fieldKey={field?.key}
          value={str}
          placeholder="Enter number"
          label={labelEl}
          onChange={onFieldChange}
          type="number"
          disabled={disabled}
        />
      );
    }

    if (t === HELPDESK_CUSTOM_FIELD_TYPES.DATE) {
      const raw = values?.[field?.key];
      const iso =
        typeof raw === "string" && raw
          ? raw
          : raw instanceof Date
            ? raw?.toISOString()
            : null;
      return (
        <div key={field?.key} className={WRAP_CLASS}>
          {labelEl}
          <PermissionDisabledWrapper disabled={disabled} className="w-max">
            <SingleDatePicker
              value={iso ? new Date(iso) : null}
              onChange={(date) =>
                onFieldChange(field?.key, date ? date?.toISOString() : null)
              }
              Icon={CalendarIcon}
              iconClassName="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk"
              inputClassName="!gap-1 text-md font-medium text-heading-text dark:text-heading-text-dk h-[26px] rounded-lg py-0"
              placeholder="Pick a date"
            />
          </PermissionDisabledWrapper>
        </div>
      );
    }
    return null;
  };

  return <>{ordered?.map((field) => renderField(field))}</>;
}
